import type { ReactNode } from 'react';

import type { ImportViolation } from '@/lib/api/generated/model';

import type { BuilderSelection } from '../builder/selection';

/**
 * Why publication was refused — every problem at once, each one selectable.
 *
 * The service reports all of them together and locates each one, which is the
 * whole reason this can be a list rather than a single message. Showing one
 * problem at a time would turn fixing a workflow into a guessing loop: fix,
 * publish, discover the next, repeat.
 *
 * Clicking a problem selects the element it is about (FR-008). A violation
 * carries `step_id` and `transition_key` precisely so a canvas can do this —
 * `path` alone is a document coordinate, which means nothing on a graph.
 */
export function ProblemList({
  violations,
  onSelect,
  actionFor,
  title = 'This workflow cannot be published yet',
}: {
  violations: ImportViolation[];
  onSelect: (selection: BuilderSelection) => void;
  /**
   * The one thing to do about a problem, beside it — "Open draft" for a linked
   * workflow that must be published first (spec 202). Most problems have none.
   */
  actionFor?: (violation: ImportViolation) => ReactNode;
  title?: string;
}) {
  if (violations.length === 0) return null;

  return (
    <section
      className="border-destructive/40 bg-destructive/5 flex flex-col gap-2 rounded-lg border p-3"
      data-testid="builder-problems"
      aria-label="Problems"
    >
      <h3 className="text-foreground text-sm font-medium">{title}</h3>
      <ul className="flex flex-col gap-1.5">
        {violations.map((violation, index) => {
          const selection = selectionFor(violation);
          const action = actionFor?.(violation);
          return (
            <li
              key={`${violation.path ?? 'none'}:${String(index)}`}
              className="flex items-start gap-2"
            >
              {selection ? (
                <button
                  type="button"
                  onClick={() => onSelect(selection)}
                  className="text-foreground hover:bg-accent focus-visible:ring-ring w-full rounded-sm px-1.5 py-1 text-left text-xs underline-offset-4 hover:underline focus-visible:ring-3 focus-visible:outline-none"
                >
                  {violation.message}
                </button>
              ) : (
                // Not everything has somewhere to go: "no path reaches Done" is
                // about the graph as a whole. Rendering it as a dead button
                // would promise a jump that cannot happen.
                <p className="text-muted-foreground px-1.5 py-1 text-xs">
                  {violation.message}
                </p>
              )}
              {action && (
                <span className="shrink-0 py-1 text-xs">{action}</span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** What this violation is about, or null when it is about the whole document. */
function selectionFor(violation: ImportViolation): BuilderSelection {
  if (violation.step_id && violation.transition_key) {
    return {
      kind: 'transition',
      stepId: violation.step_id,
      key: violation.transition_key,
    };
  }
  if (violation.step_id) return { kind: 'step', id: violation.step_id };
  if (violation.phase_id) return { kind: 'phase', id: violation.phase_id };
  return null;
}
